'use client';
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import type { SlotComponentProps } from '@mui/utils/types';
import type { ChatConversation } from '../types/chat-entities';
import type { ConversationListUnreadBadgeOwnerState } from './conversationList.types';

export interface ConversationListUnreadBadgeSlots {
  /**
   * The component that renders the root.
   */
  root: React.ElementType;
}

export interface ConversationListUnreadBadgeSlotProps {
  /**
   * The props used for the root slot.
   */
  root?: SlotComponentProps<'span', {}, ConversationListUnreadBadgeOwnerState>;
}

export interface ConversationListUnreadBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The conversation the item renders.
   */
  conversation: ChatConversation;
  /**
   * If `true`, the conversation is selected.
   */
  selected?: boolean;
  /**
   * If `true`, the conversation has unread messages.
   */
  unread?: boolean;
  /**
   * If `true`, the item has keyboard focus.
   */
  focused?: boolean;
  /**
   * Overridable component slots.
   * @default {}
   */
  slots?: Partial<ConversationListUnreadBadgeSlots>;
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps?: ConversationListUnreadBadgeSlotProps;
}

function formatUnreadCount(count: number): string | number {
  return count > 99 ? '99+' : count;
}

type ConversationListUnreadBadgeComponent = ((
  props: ConversationListUnreadBadgeProps & React.RefAttributes<HTMLSpanElement>,
) => React.JSX.Element | null) & { propTypes?: any };

export const ConversationListUnreadBadge = React.forwardRef(function ConversationListUnreadBadge(
  props: ConversationListUnreadBadgeProps,
  ref: React.Ref<HTMLSpanElement>,
) {
  const {
    conversation,
    selected = false,
    unread = false,
    focused = false,
    ownerState: ownerStateProp,
    slots,
    slotProps,
    ...other
  } = props as ConversationListUnreadBadgeProps & {
    ownerState?: ConversationListUnreadBadgeOwnerState;
  };
  const ownerState: ConversationListUnreadBadgeOwnerState = {
    conversation,
    selected,
    unread,
    focused,
    variant: ownerStateProp?.variant ?? 'default',
  };
  void ownerStateProp;

  const Root = slots?.root ?? 'span';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref,
    },
  });

  const unreadCount =
    conversation.unreadCount != null && conversation.unreadCount > 0
      ? conversation.unreadCount
      : null;

  // In compact mode, render a dot indicator for any unread state (even without a numeric count).
  if (ownerState.variant === 'compact') {
    if (!unread) {
      return null;
    }
    return <Root {...rootProps}>{unreadCount != null ? formatUnreadCount(unreadCount) : ''}</Root>;
  }

  if (!unreadCount) {
    return null;
  }

  return <Root {...rootProps}>{unreadCount > 99 ? '99+' : unreadCount}</Root>;
}) as ConversationListUnreadBadgeComponent;
